import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getTenantList, getTenantDetails, createTenant, updateTenant, deleteTenant } from '../API/tenantApi';
import type { RootState } from '../store/store';

interface TenantConfiguration {
    maxUsers: number;
    maxDataSources: number;
    features: string[];
    customProperties: {
        [key: string]: string;
    };
}

export interface ITenant {
    id?: string;
    name: string;
    fullyQualifiedName: string;
    displayName: string;
    description: string;
    status: 'active' | 'inactive' | 'suspended';
    plan: string;
    contactEmail: string;
    adminUsers: string[];
    configuration: TenantConfiguration;
    updatedAt?: number;
    updatedBy?: string;
    createdBy?: string;
    href?: string;
    version?: number;
    deleted?: boolean;
}

interface TenantState {
    tenantList: ITenant[];
    tenantDetails: ITenant;
}

export const initialTenantForm: ITenant = {
    name: "",
    fullyQualifiedName: "",
    displayName: "",
    description: "",
    status: "active",
    plan: "standard",
    contactEmail: "",
    adminUsers: [],
    configuration: {
        maxUsers: 100,
        maxDataSources: 10,
        features: [],
        customProperties: {}
    }
};

const initialState: TenantState = {
    tenantList: [],
    tenantDetails: initialTenantForm
};

export const callGetTenantList = createAsyncThunk(
    'tenants/getTenantList',
    async (args, { dispatch }) => {
        const response = await getTenantList();
        dispatch(setTenantList(response.data.data));
    }
);

export const callGetTenantDetails = createAsyncThunk(
    'tenants/getTenantDetails',
    async (tenantId: string, { dispatch }) => {
        const response = await getTenantDetails(tenantId);
        dispatch(setTenantDetails(response.data));
    }
);

export const callCreateTenant = createAsyncThunk(
    'tenants/createTenant',
    async (tenant: ITenant, { dispatch }) => {
        const response = await createTenant({ ...tenant, fullyQualifiedName: tenant.name });
        return response.data.id;
    }
);

export const callUpdateTenant = createAsyncThunk<any, any, { state: RootState }>(
    'tenants/updateTenant',
    async (tenant: ITenant, { getState }) => {
        const response = await updateTenant(tenant);
        return response.data.id;
    }
);

export const callDeleteTenant = createAsyncThunk(
    'tenants/deleteTenant',
    async (id: string, { dispatch }) => {
        const response = await deleteTenant(id);
        dispatch(callGetTenantList());
        return response.data;
    }
);

export const tenantSlice = createSlice({
    name: 'tenant',
    initialState,
    reducers: {
        setTenantList: (state, action: PayloadAction<ITenant[]>) => {
            const removeDeletedTenants = action.payload
                .filter(tenant => tenant.deleted !== true)
                .sort((a, b) => a.name.localeCompare(b.name));
            state.tenantList = removeDeletedTenants;
        },
        setTenantDetails: (state, action: PayloadAction<ITenant>) => {
            state.tenantDetails = action.payload;
        }
    }
});

export const {
    setTenantList,
    setTenantDetails
} = tenantSlice.actions;

export const selectTenantList = (state: RootState): ITenant[] => state.tenant.tenantList;
export const selectTenantDetails = (state: RootState): ITenant => state.tenant.tenantDetails;

export default tenantSlice.reducer;
